import { CookieLibrary } from '@/core/apis/browser_storage_api/cookie_lib/cookie_lib';
import { Logger } from '@/shared/logger/logger';

type GaParts = {
  domainDepth: string;
  timestamp: string;
  userId: string;
  version: string;
};

type GaCidParts = {
  domainDepth: string;
  hitTimestamp: string;
  lastTimestamp: string;
  sessionEngagement: string;
  sessionId: string;
  sessionNumber: string;
  version: string;
};

export const Ga4CookieLogic: {
  cookiesToUpdate: string[];
  getClientId: () => string | null;
  getGaCidCookie: (measurementId: string) => string | null;
  getGaCidCookieKey: (measurementId: string) => string;
  getGaCookie: () => string | null;
  getSessionId: (measurementId: string) => string | null;
  isValidGaCidCookie: (cookie: string) => boolean;
  isValidGaCookie: (cookie: string) => boolean;
  parseCidCookieIntoParts: (cookie: string) => GaCidParts | null;
  parseGaCidCookie: (measurementId: string) => GaCidParts | null;
  parseGaCookie: () => GaParts | null;
  parseGaCookieIntoParts: (cookie: string) => GaParts | null;
  safeGetCookiePart: (cookie: string, index: number) => string;
  updateCookies: (
    _cookies: Record<string, string>,
    _iqpars: Record<string, string[]>,
    measurementId: string,
    cookieKeysToUpdate: string[],
  ) => Record<string, string>;
} = {
  cookiesToUpdate: ['_ga', '_ga_cid'],
  // Function to get client ID from GA cookie
  getClientId: (): string | null => {
    const gaParts = Ga4CookieLogic.parseGaCookie();
    return gaParts ? `${gaParts.userId}.${gaParts.timestamp}` : null;
  },
  getGaCidCookie: (measurementId: string): string | null =>
    CookieLibrary.get(Ga4CookieLogic.getGaCidCookieKey(measurementId)),

  getGaCidCookieKey: (measurementId: string): string => `_ga_${measurementId}`,

  getGaCookie: (): string | null => CookieLibrary.get('_ga'),

  // Function to get session ID from GA CID cookie
  getSessionId: (measurementId: string): string | null => {
    const gaCidParts = Ga4CookieLogic.parseGaCidCookie(measurementId);
    return gaCidParts?.sessionId ?? null;
  },

  // New function to validate GA CID cookie format
  isValidGaCidCookie: (cookie: string): boolean => {
    const parts = cookie.split('.');
    return parts.length >= 7 && parts.every(part => part.trim() !== '');
  },

  // New function to validate GA cookie format
  isValidGaCookie: (cookie: string): boolean => {
    const parts = cookie.split('.');
    return parts.length >= 4 && parts.every(part => part.trim() !== '');
  },

  parseCidCookieIntoParts: (cookie: string): GaCidParts | null => {
    const parts = cookie.split('.');
    if (parts.length < 7) {
      Logger.error('Invalid GA CID cookie format');
      return null;
    }

    return {
      domainDepth: parts[1] ?? '',
      hitTimestamp: parts[6] ?? '',
      lastTimestamp: parts[5] ?? '',
      sessionEngagement: parts[4] ?? '',
      sessionId: parts[2] ?? '',
      sessionNumber: parts[3] ?? '',
      version: parts[0] ?? '',
    };
  },

  // Helper function to validate and parse GA CID cookie
  parseGaCidCookie: (measurementId: string): GaCidParts | null => {
    const gaCidCookie = Ga4CookieLogic.getGaCidCookie(measurementId);
    return gaCidCookie
      ? Ga4CookieLogic.parseCidCookieIntoParts(gaCidCookie)
      : null;
  },

  // Helper function to validate and parse GA cookie
  parseGaCookie: (): GaParts | null => {
    const gaCookie = Ga4CookieLogic.getGaCookie();
    return gaCookie ? Ga4CookieLogic.parseGaCookieIntoParts(gaCookie) : null;
  },

  parseGaCookieIntoParts: (cookie: string): GaParts | null => {
    const parts = cookie.split('.');
    if (parts.length < 4) {
      Logger.error('Invalid GA cookie format');
      return null;
    }

    return {
      domainDepth: parts[1] ?? '',
      timestamp: parts[3] ?? '',
      userId: parts[2] ?? '',
      version: parts[0] ?? '',
    };
  },

  // New function to safely get a part from a cookie string
  safeGetCookiePart: (cookie: string, index: number): string => {
    const parts = cookie.split('.');
    return parts[index] ?? '';
  },

  updateCookies(
    _cookies: Record<string, string>,
    _iqpars: Record<string, string[]>,
    measurementId: string,
    cookieKeysToUpdate: string[] = Ga4CookieLogic.cookiesToUpdate,
  ): Record<string, string> {
    const newCookies: Record<string, string> = {};

    if (cookieKeysToUpdate?.includes('_ga')) {
      const gaCookie = Ga4CookieLogic.getGaCookie();
      if (gaCookie) {
        newCookies['_ga'] = gaCookie;
      }
    }

    if (cookieKeysToUpdate?.includes('_ga_cid')) {
      const gaCidCookie = Ga4CookieLogic.getGaCidCookie(measurementId);
      if (gaCidCookie) {
        newCookies[Ga4CookieLogic.getGaCidCookieKey(measurementId)] =
          gaCidCookie;
      }
    }
    return newCookies;
  },
};
